import { useCallback, useEffect, useMemo, useRef } from 'react'
import { Map, Source, Layer } from 'react-map-gl'
import Spinner from 'react-bootstrap/Spinner'
import MapImages from '../components/MapImages'
import { useFlyTo, useIsMobileDevice, useSource } from '../helpers/hooks'
import { useAuthState, useMapState } from '../state/context'
import {
	PINS_ABORT_ERROR,
	PINS_FETCH_ERROR,
	authenticateSession,
	getPins,
	locateUser,
	locateUserCountry,
	retrieveStoredMapView,
	setStoredFilterButtonToggleOption,
	setStoredFilterOption,
	setStoredMapView,
} from '../utility/api'
import {
	receivePins,
	setSelectedPin,
	updateViewState,
	fetchingPins,
	checkingLocationAction,
	receiveSavedView,
	receivePinsFetchError,
	updateRunningInitialFetch,
	setActivePin,
	receiveChargersInViewport,
	updateAuthenticating,
	updateShouldAuthenticate,
	toggleExpandedFilterButton,
	updateNonTeslaOnly,
} from '../state/actions'
import gs from '../utility/styles'
import MapList from '../components/MapList'
import styled, { css } from 'styled-components'
import styles from '../utility/styles'
import Button from '../components/shared/Button'
import { useNavigate, useParams } from 'react-router-dom'
import {
	parsePotentialJSON,
	returnPinsWithinViewport,
} from '../utility/helpers'
import MapPopup from '../components/MapPopup'
import FilterButton from '../components/chargerScreen/FilterButton'
import { returnSource } from '../utility/geojson'

// Mapbox GL paint properties can't parse CSS var() expressions (used by the
// themed color tokens), so resolve a `var(--token, #fallback)` string down to
// its literal fallback color for use in map layers.
const mapboxColor = value => {
	const match = /var\([^,]+,\s*([^)]+)\)/.exec(String(value))
	return match ? match[1].trim() : value
}

const Wrapper = styled.div`
	display: flex;
	padding-left: ${styles.spacing.navBar};
	position: relative;
	background-color: ${styles.colors.background};
	@media screen and ${styles.breakpoints.desktop} {
		padding-left: 0;
		display: block;
	}
`

const MapWrapper = styled.div`
	width: 100lvw;
	height: 100lvh;
	@media screen and ${styles.breakpoints.desktop} {
		height: calc(100lvh - 64px - 180px);
		// width: 100%;
	}
	@media screen and (max-width: 450px), screen and (max-height: 450px) {
		height: calc(100lvh - 64px);
	}
	& .mapboxgl-ctrl-bottom-left,
	.mapboxgl-ctrl-bottom-right {
		bottom: calc(100lvh - 100dvh);
	}
`

const StyledSpinner = styled(Spinner)`
	position: absolute;
	right: 58px;
	top: ${({ filterExpanded }) => (filterExpanded ? '160px' : '92px')};
	@media screen and ${styles.breakpoints.desktop} {
		top: calc(180px + 34px);
		left: 24px;
	}
	@media screen and (max-width: 450px) {
		top: 24px;
		left: 24px;
	}
	@media screen and (max-height: 450px) {
		top: 24px;
		left: calc(8px + env(safe-area-inset-left));
	}
`

const Mask = styled.div`
	position: absolute;
	top: 0;
	right: 0;
	left: 0;
	bottom: 0;
	background-color: ${({ isTransparent }) =>
		isTransparent ? 'transparent' : styles.colors.background};
`

const ButtonGroup = styled.div`
	position: absolute;
	right: 24px;
	bottom: 48px;
	display: flex;
	flex-direction: column;
	@media screen and ${styles.breakpoints.desktop} {
		bottom: calc(100lvh - 100dvh + 48px);
		transition: 0.3s bottom;
	}
	@media screen and (max-height: 450px) {
		right: calc(8px + env(safe-area-inset-right));
	}
`

const middle = css`
	border-bottom: 1px solid
		${styles.functions.opacityModifier(styles.colors.mediumGray, 50)};
	border-top: 1px solid
		${styles.functions.opacityModifier(styles.colors.mediumGray, 50)};
`

const top = css`
	border-top-left-radius: 4px;
	border-top-right-radius: 4px;
`

const bottom = css`
	border-bottom-left-radius: 4px;
	border-bottom-right-radius: 4px;
`

const cssVariant = {
	top,
	bottom,
	middle,
}

const StyledButton = styled(Button)`
	padding: 8px;
	background-color: ${styles.colors.white};
	border-radius: 0;
	${({ variant }) => cssVariant[variant]};
	@media screen and (max-width: 400px), screen and (max-height: 450px) {
		display: ${({ variant }) => (variant === 'bottom' ? 'block' : 'none')};
		border-radius: 4px;
	}
`

export default function MapScreen() {
	const [
		{
			viewState,
			savedView,
			pins: pinsArray,
			activePin,
			fetchingPins: isFetchingPins,
			pinsFetchError,
			checkingLocation,
			loadMap,
			runningInitialFetch,
			selectedPin,
			filterExpanded,
			showNonTeslaOnly,
		},
		dispatch,
	] = useMapState()
	const [{ shouldAuthenticate }, authDispatch] = useAuthState()
	const navigate = useNavigate()
	const isMobile = useIsMobileDevice()
	const { id } = useParams()
	const flyTo = useFlyTo({ duration: 200 })
	const map = useRef()
	const abort = useRef()
	const source = useSource(pinsArray, showNonTeslaOnly)
	const largePin = useMemo(
		() => (activePin ? activePin : selectedPin ? selectedPin : ''),
		[activePin, selectedPin]
	)

	const showMask = useMemo(() => !loadMap, [loadMap])

	// filter
	const expand = useCallback(
		() => dispatch(toggleExpandedFilterButton(true)),
		[dispatch]
	)
	const closeIt = useCallback(() => {
		dispatch(toggleExpandedFilterButton(false))
		setStoredFilterButtonToggleOption(false)
	}, [dispatch])
	const toggle = useCallback(() => {
		const bool = !showNonTeslaOnly
		setStoredFilterOption(bool)
		dispatch(updateNonTeslaOnly(bool))
	}, [dispatch, showNonTeslaOnly])

	const returnPinList = useCallback((pins, bounds) => {
		if (!Array.isArray(pins)) return []
		const pinsArray = showNonTeslaOnly ? pins.filter(pin => pin.sc_non_tesla) : pins
		const boundarySource = returnSource(pinsArray)
		const list = returnPinsWithinViewport(boundarySource, bounds)
		return list
	}, [showNonTeslaOnly])

	const fetchPins = useCallback(
		async (
			latitude,
			longitude,
			cornerOneLat,
			cornerOneLon,
			cornerTwoLat,
			cornerTwoLon
		) => {
			try {
				// abort ongoing fetch
				dispatch(fetchingPins(true))
				if (runningInitialFetch)
					dispatch(updateRunningInitialFetch(false)) // initial location fetch has run and the map is moving
				if (typeof abort.current === 'function') {
					abort.current()
					abort.current = null
				}
				const { promise, abort: abortFn } = getPins(
					latitude,
					longitude,
					cornerOneLat,
					cornerOneLon,
					cornerTwoLat,
					cornerTwoLon
				)
				abort.current = abortFn
				const { data: pins, error } = await promise()
				if (error) throw error
				const ne = [cornerOneLon, cornerOneLat]
				const sw = [cornerTwoLon, cornerTwoLat]
				const within = returnPinList(pins, { ne, sw })
				dispatch(receiveChargersInViewport(within))
				dispatch(receivePins(pins))
				dispatch(fetchingPins(false))
			} catch (error) {
				if (error === PINS_ABORT_ERROR) return
				dispatch(receiveChargersInViewport([]))
				dispatch(receivePinsFetchError(PINS_FETCH_ERROR))
				dispatch(fetchingPins(false))
			}
		},
		[dispatch, returnPinList, runningInitialFetch]
	)

	// used to re-fetch pins when the map moves
	const onMoveEnd = useCallback(
		async ({ viewState }) => {
			dispatch(updateViewState(viewState)) // will probably use this later on somewhere
			const bounds = map.current?.getBounds()
			console.log('bounds', { bounds })
			const cornerOneLat = bounds?._ne.lat
			const cornerOneLon = bounds?._ne.lng
			const cornerTwoLat = bounds?._sw.lat
			const cornerTwoLon = bounds?._sw.lng
			await fetchPins(
				viewState.latitude,
				viewState.longitude,
				cornerOneLat,
				cornerOneLon,
				cornerTwoLat,
				cornerTwoLon
			)
		},
		[dispatch, fetchPins]
	)

	const zoomToExpansion = feature => {
		if (!feature?.properties || !feature?.geometry) return
		const { cluster_id } = feature.properties
		map.current
			?.getSource('markers')
			?.getClusterExpansionZoom(cluster_id, (err, zoom) => {
				try {
					if (err) throw err
					map.current?.easeTo({
						center: feature.geometry.coordinates,
						zoom,
					})
				} catch (error) {
					console.warn('zoomToExpansion error', { error })
				}
			})
	}

	const navigateToCharger = useCallback(
		(id, longitude, latitude) => {
			navigate(`/charger/${id}`)
			flyTo([longitude, latitude])
		},
		[navigate, flyTo]
	)

	// used to navigate to charger or zoom in
	const onClick = useCallback(
		({ features }) => {
			if (!Array.isArray(features) || !features.length) {
				dispatch(setSelectedPin(''))
				dispatch(setActivePin(''))
				return
			}
			const feature = features[0]
			if (feature?.properties?.cluster_id) return zoomToExpansion(feature)
			const id = feature?.properties?.id
			const location = parsePotentialJSON(feature?.properties?.location)
			const { lat, lon } = location
			if (id && typeof lon === 'number' && typeof lat === 'number') {
				// if it's a mobile device, activate the tooltip thing unless the tooltip is active on this pin
				isMobile && activePin !== id
					? dispatch(setActivePin(id))
					: navigateToCharger(id, lon, lat)
			}
		},
		[dispatch, activePin, isMobile, navigateToCharger]
	)

	const onTooltipClick = useCallback(
		(id, lon, lat) => navigateToCharger(id, lon, lat),
		[navigateToCharger]
	)

	const onMouseEnter = useCallback(
		({ features }) => {
			if (Array.isArray(features)) {
				const feature = features[0]
				const id = feature?.properties?.id ?? ''
				dispatch(setSelectedPin(id))
				dispatch(setActivePin(id))
			}
		},
		[dispatch]
	)

	const onLoad = useCallback(async () => {
		const saved = retrieveStoredMapView()
		const bounds = map.current?.getBounds()
		const cornerOneLat = bounds?._ne.lat
		const cornerOneLon = bounds?._ne.lng
		const cornerTwoLat = bounds?._sw.lat
		const cornerTwoLon = bounds?._sw.lng
		try {
			// if there's an id it means somebody clicked on the fake map
			if (shouldAuthenticate) {
				authDispatch(updateAuthenticating(true))
				await authenticateSession()
				authDispatch(updateAuthenticating(false))
				authDispatch(updateShouldAuthenticate(false))
			}
			if (id) {
				// fetch pins
				dispatch(fetchingPins(true))
				dispatch(updateRunningInitialFetch(false)) // initial location fetch has run and the map is moving
				const latitude = savedView?.center[1]
				const longitude = savedView?.center[0]
				const { promise, abort: abortFn } = getPins(
					latitude,
					longitude,
					cornerOneLat,
					cornerOneLon,
					cornerTwoLat,
					cornerTwoLon
				)
				abort.current = abortFn
				const { data: pins, error } = await promise()
				if (error) throw error
				dispatch(receivePins(pins))
				dispatch(fetchingPins(false))
				// reset state
				dispatch(receiveSavedView(null))
			} else {
				if (saved)
					return fetchPins(
						saved.latitude,
						saved.longitude,
						cornerOneLat,
						cornerOneLon,
						cornerTwoLat,
						cornerTwoLon
					) // just get pins from saved view on map load!
				dispatch(checkingLocationAction(true))
				const { longitude, latitude, zoom, error } = await locateUser()
				if (error) throw error
				setStoredMapView({ longitude, latitude, zoom })
				dispatch(checkingLocationAction(false))
				flyTo([longitude, latitude], { zoom })
			}
		} catch (error) {
			// locate the user's country instead as a fallback
			const { center, zoom } = await locateUserCountry()
			setStoredMapView({
				longitude: center[0],
				latitude: center[1],
				zoom,
			})
			flyTo(center, { zoom })
			dispatch(checkingLocationAction(false))
		}
	}, [
		id,
		dispatch,
		savedView?.center,
		fetchPins,
		flyTo,
		shouldAuthenticate,
		authDispatch,
	])

	// used when interacting with the button group

	const flyToUser = async () => {
		try {
			dispatch(checkingLocationAction(true))
			const { latitude, longitude, zoom, error } = await locateUser()
			if (error) throw error
			setStoredMapView({ longitude, latitude, zoom })
			dispatch(checkingLocationAction(false))
			flyTo([longitude, latitude], { zoom })
		} catch (error) {
			dispatch(checkingLocationAction(false))
		}
	}

	const zoomIn = () => {
		map.current?.zoomIn()
	}

	const zoomOut = () => {
		map.current?.zoomOut()
	}

	useEffect(() => {
		const updateList = () => {
			try {
				const bounds = map.current?.getBounds()
				const cornerOneLat = bounds?._ne.lat
				const cornerOneLon = bounds?._ne.lng
				const cornerTwoLat = bounds?._sw.lat
				const cornerTwoLon = bounds?._sw.lng
				const ne = [cornerOneLon, cornerOneLat]
				const sw = [cornerTwoLon, cornerTwoLat]
				const within = returnPinList(pinsArray, { ne, sw })
				dispatch(receiveChargersInViewport(within))
			} catch (error) {
				
			}
		}
		updateList()
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [dispatch, showNonTeslaOnly])

	return (
		<Wrapper>
			<MapList navigate={navigateToCharger} />
			{loadMap && (
				<MapWrapper>
					<Map
						mapboxAccessToken={
							process.env.REACT_APP_MAPBOX_ACCESS_TOKEN
						}
						style={{ width: '100%', height: '100%' }}
						mapStyle='mapbox://styles/tezlabapp/ckr2rnuwb13p917takt5n7nta'
						ref={map}
						{...{ onLoad, onClick, onMoveEnd, onMouseEnter }}
						initialViewState={
							savedView
								? {
										latitude: savedView.center[1],
										longitude: savedView.center[0],
										zoom: savedView.zoom,
								  }
								: viewState
						}
						interactiveLayerIds={['map_pins', 'circle']}
						logoPosition={'bottom-left'}
						id='chargerMap'
					>
						<MapImages />
						<MapPopup {...{ activePin, onTooltipClick }} />
						<Source
							id='markers'
							type='geojson'
							data={source}
							cluster
							clusterRadius={50}
						>
							<Layer
								type='symbol'
								layout={{
									'icon-image': ['get', 'pin_type'],
									'icon-anchor': 'bottom',
									'icon-size': [
										'case',
										['==', ['get', 'id'], largePin],
										0.3,
										0.2,
									],
									'icon-allow-overlap': true,
									'icon-rotation-alignment': 'viewport',
									'icon-ignore-placement': true,
								}}
								id='map_pins'
								filter={['!', ['has', 'point_count']]}
							/>
							<Layer
								type='circle'
								id='circle'
								paint={{
									'circle-radius': 24,
									'circle-color': mapboxColor(gs.colors.blue),
									'circle-stroke-width': 3,
									'circle-stroke-color': mapboxColor(gs.colors.white),
									'circle-pitch-alignment': 'map',
								}}
								filter={['has', 'point_count']}
							/>
							<Layer
								type='symbol'
								id='point_count'
								filter={['has', 'point_count']}
								paint={{
									'text-color': mapboxColor(gs.colors.white),
								}}
								layout={{
									'text-field': '{point_count}',
									'text-size': 14,
									'text-pitch-alignment': 'map',
									'text-allow-overlap': true,
								}}
							/>
						</Source>
					</Map>
				</MapWrapper>
			)}
			{loadMap && (
				<ButtonGroup>
					<StyledButton variant='top' onClick={zoomIn}>
						<svg
							xmlns='http://www.w3.org/2000/svg'
							width='24'
							height='24'
							viewBox='0 0 24 24'
							fill='none'
						>
							<path
								fillRule='evenodd'
								clipRule='evenodd'
								d='M12 2C11.4477 2 11 2.44772 11 3V11H3C2.44772 11 2 11.4477 2 12C2 12.5523 2.44772 13 3 13H11V21C11 21.5523 11.4477 22 12 22C12.5523 22 13 21.5523 13 21V13H21C21.5523 13 22 12.5523 22 12C22 11.4477 21.5523 11 21 11H13V3C13 2.44772 12.5523 2 12 2Z'
								fill={styles.colors.background}
							/>
						</svg>
					</StyledButton>
					<StyledButton variant='middle' onClick={zoomOut}>
						<svg
							xmlns='http://www.w3.org/2000/svg'
							width='24'
							height='24'
							viewBox='0 0 24 24'
							fill='none'
						>
							<path
								d='M3 13C2.44772 13 2 12.5523 2 12C2 11.4477 2.44772 11 3 11H21C21.5523 11 22 11.4477 22 12C22 12.5523 21.5523 13 21 13H3Z'
								fill={styles.colors.background}
							/>
						</svg>
					</StyledButton>
					<StyledButton variant='bottom' onClick={flyToUser}>
						{checkingLocation ? (
							<Spinner
								variant='secondary'
								style={{ height: '24px', width: '24px' }}
							/>
						) : (
							<svg
								xmlns='http://www.w3.org/2000/svg'
								width='24'
								height='24'
								viewBox='0 0 24 24'
								fill='none'
							>
								<path
									fillRule='evenodd'
									clipRule='evenodd'
									d='M3.37416 10.4837C2.53606 10.8506 2.59004 12.0572 3.45755 12.3479L9.20775 14.2746L11.3182 20.2195C11.6239 21.0807 12.8293 21.1145 13.1827 20.2718L19.4507 5.32655C19.7999 4.49406 18.9544 3.66166 18.1274 4.02375L3.37416 10.4837ZM10.3049 12.5329L6.55211 11.2755L16.6394 6.85864L12.3381 17.1144L10.9296 13.1466C10.8268 12.8569 10.5964 12.6306 10.3049 12.5329Z'
									fill={styles.colors.background}
								/>
							</svg>
						)}
					</StyledButton>
				</ButtonGroup>
			)}
			<FilterButton
				isExpanded={filterExpanded}
				isNonTeslaOnly={showNonTeslaOnly}
				{...{ expand, toggle, closeIt }}
			/>
			{isFetchingPins && <StyledSpinner {...{ filterExpanded }} variant='secondary' />}
			{showMask && <Mask isTransparent={loadMap} />}
		</Wrapper>
	)
}
